import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Package, ShoppingCart, AlertTriangle, 
  Clock, DollarSign, BarChart3, Calendar, ArrowRight, Zap,
  RefreshCw
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedDashboardWidgets = ({ user, onNavigate }) => {
  const [summary, setSummary] = useState(null);
  const [salesTrend, setSalesTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendPeriod, setTrendPeriod] = useState('weekly');

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [summaryRes, trendRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard-summary`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }),
        axios.get(`${API}/analytics/sales-trend?period=${trendPeriod}`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        })
      ]);
      setSummary(summaryRes.data);
      setSalesTrend(trendRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [trendPeriod]);

  // Listen for pull-to-refresh event from parent
  useEffect(() => {
    const handler = () => fetchDashboardData();
    window.addEventListener('app:refresh-data', handler);
    return () => window.removeEventListener('app:refresh-data', handler);
  }, []);

  // Navigation handler for clickable cards
  const handleCardClick = (cardType) => {
    if (onNavigate) {
      onNavigate(cardType);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value?.toLocaleString() || 0}`;
  };

  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value?.toLocaleString() || 0;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 animate-pulse">
            <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-slate-200 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Stock Value */}
        <button
          type="button"
          onClick={() => handleCardClick('merged')}
          data-testid="dash-card-stock-value"
          className="text-left bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Stock Value</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(summary?.stock_value?.total || 0)}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-blue-100">
                <span>SRH: {formatCurrency(summary?.stock_value?.srh || 0)}</span>
                <span>•</span>
                <span>WL: {formatCurrency(summary?.stock_value?.winlife || 0)}</span>
              </div>
            </div>
            <div className="bg-white/20 p-3 rounded-xl">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
        </button>

        {/* Total Products */}
        <button
          type="button"
          onClick={() => handleCardClick('merged')}
          data-testid="dash-card-total-products"
          className="text-left bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">Total Products</p>
              <p className="text-2xl font-bold mt-1">{formatNumber(summary?.total_products || 0)}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-emerald-100">
                <span>SRH: {formatNumber(summary?.stock_qty?.srh || 0)} units</span>
              </div>
            </div>
            <div className="bg-white/20 p-3 rounded-xl">
              <Package className="w-6 h-6" />
            </div>
          </div>
        </button>

        {/* Pending Orders */}
        <button
          type="button"
          onClick={() => handleCardClick('orders')}
          data-testid="dash-card-pending-orders"
          className="text-left bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm font-medium">Pending Orders</p>
              <p className="text-2xl font-bold mt-1">{summary?.pending_orders?.count || 0}</p>
              <p className="text-xs text-amber-100 mt-2">
                Est. Value: {formatCurrency(summary?.pending_orders?.estimated_value || 0)}
              </p>
            </div>
            <div className="bg-white/20 p-3 rounded-xl">
              <ShoppingCart className="w-6 h-6" />
            </div>
          </div>
        </button>

        {/* Completed This Month */}
        <button
          type="button"
          onClick={() => handleCardClick('orders')}
          data-testid="dash-card-completed-month"
          className="text-left bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Completed This Month</p>
              <p className="text-2xl font-bold mt-1">{summary?.completed_this_month || 0}</p>
              <p className="text-xs text-purple-100 mt-2">
                Orders delivered
              </p>
            </div>
            <div className="bg-white/20 p-3 rounded-xl">
              <Calendar className="w-6 h-6" />
            </div>
          </div>
        </button>
      </div>

      {/* Second Row - Alerts and Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-800">Order Completion Trend</h3>
              <p className="text-xs text-slate-500">Completed orders over time</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTrendPeriod('weekly')}
                className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                  trendPeriod === 'weekly' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => setTrendPeriod('monthly')}
                className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                  trendPeriod === 'monthly' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={fetchDashboardData}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="h-64">
            {salesTrend?.trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salesTrend.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                <p>No trend data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Alerts Panel */}
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Alerts & Notifications
          </h3>
          
          <div className="space-y-3">
            {/* Low Stock Alert */}
            <button
              type="button"
              onClick={() => handleCardClick('merged')}
              data-testid="dash-alert-low-stock"
              className="w-full text-left flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100 hover:bg-red-100 transition-colors cursor-pointer"
            >
              <div className="p-2 bg-red-100 rounded-lg">
                <Package className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Low Stock Items</p>
                <p className="text-xs text-red-600">{summary?.low_stock_count || 0} products below 50 units</p>
              </div>
              <ArrowRight className="w-4 h-4 text-red-400" />
            </button>

            {/* Expiring Soon */}
            {summary?.expiring_soon?.length > 0 && (
              <button
                type="button"
                onClick={() => handleCardClick('merged')}
                data-testid="dash-alert-expiring"
                className="w-full text-left p-3 bg-amber-50 rounded-lg border border-amber-100 hover:bg-amber-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Expiring Soon</span>
                  <ArrowRight className="w-4 h-4 text-amber-400 ml-auto" />
                </div>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {summary.expiring_soon.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-amber-700 truncate flex-1">{item.product_name}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded font-medium ${
                        item.days_left <= 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {item.days_left}d
                      </span>
                    </div>
                  ))}
                </div>
              </button>
            )}

            {/* Pending Orders */}
            {summary?.pending_orders?.count > 0 && (
              <button
                type="button"
                onClick={() => handleCardClick('orders')}
                data-testid="dash-alert-pending-orders"
                className="w-full text-left flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors cursor-pointer"
              >
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ShoppingCart className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">Pending Orders</p>
                  <p className="text-xs text-blue-600">{summary.pending_orders.count} orders awaiting delivery</p>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Top Moving Products */}
      {summary?.top_moving_products?.length > 0 && (
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              Top Moving Products (by Sales)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Rank</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Product</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600">Packing</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Sales</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600">Stock</th>
                </tr>
              </thead>
              <tbody>
                {summary.top_moving_products.map((product, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-slate-200 text-slate-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {idx + 1}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-sm font-medium text-slate-800">{product.product_name}</td>
                    <td className="py-2 px-3 text-sm text-slate-600">{product.packing || '-'}</td>
                    <td className="py-2 px-3 text-sm text-right font-semibold text-green-600">{product.sale}</td>
                    <td className="py-2 px-3 text-sm text-right text-slate-600">{product.available_qty || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedDashboardWidgets;
